import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axios from 'axios';
import { API_BASE_URL } from '../types';

interface PostageInvoice {
  id: number;
  week_ending: string;
  filename: string;
  total_postage: number;
  total_units: number;
  total_orders: number;
  postage_per_unit: number;
  created_at: string;
}

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals);
}

function fmtDate(iso: string): string {
  // iso = "2026-02-21"
  const [year, month, day] = iso.split('-');
  return `${parseInt(month)}/${parseInt(day)}/${year}`;
}

export default function PostagePerUnit() {
  const { getToken } = useAuth();

  const [history, setHistory] = useState<PostageInvoice[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<PostageInvoice | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const token = await getToken({ template: "covervision-backend" });
      const res = await axios.get<PostageInvoice[]>(`${API_BASE_URL}/postage/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data);
    } catch (err: any) {
      setHistoryError(err?.response?.data?.detail || 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFile = async (file: File) => {
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setUploadError('Please select an Excel file (.xlsx or .xls).');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = await getToken({ template: "covervision-backend" });
      const res = await axios.post<PostageInvoice>(
        `${API_BASE_URL}/postage/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 200000, // 200s — CashMonkey can take 60-150s for large files
        }
      );
      setUploadResult(res.data);
      await fetchHistory();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setUploadError(typeof detail === 'string' ? detail : 'Upload failed. Check server logs.');
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = ''; // reset so same file can be re-selected
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Postage Per Unit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a weekly OSM Worldwide postage invoice to calculate cost-per-unit shipped.
      </Typography>

      {/* Upload card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Invoice
          </Typography>

          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              bgcolor: dragOver ? 'action.hover' : 'background.paper',
              transition: 'all 0.15s ease',
              '&:hover': uploading ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            {uploading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Processing… fetching CashMonkey data (may take 1–2 min for large files)
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <UploadFileIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                <Typography variant="body1">
                  Drag & drop an OSM invoice here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Accepts .xlsx files — filename must contain "Weekending M.DD.YY"
                </Typography>
              </Box>
            )}
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={onFileChange}
          />

          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}

          {uploadResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box>
                <strong>Week ending {fmtDate(uploadResult.week_ending)}</strong>
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="h5" component="span" sx={{ fontWeight: 700, color: 'success.dark' }}>
                    ${fmt(uploadResult.postage_per_unit, 4)} / unit
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {uploadResult.total_units.toLocaleString()} units &nbsp;|&nbsp;
                  {uploadResult.total_orders.toLocaleString()} orders &nbsp;|&nbsp;
                  ${fmt(uploadResult.total_postage)} total postage
                </Typography>
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* History table */}
      <Typography variant="h6" gutterBottom>
        History
      </Typography>

      {historyLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : historyError ? (
        <Alert severity="error">{historyError}</Alert>
      ) : history.length === 0 ? (
        <Typography color="text.secondary">No invoices uploaded yet.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Week Ending</strong></TableCell>
                <TableCell align="right"><strong>Postage / Unit</strong></TableCell>
                <TableCell align="right"><strong>Total Units</strong></TableCell>
                <TableCell align="right"><strong>Orders Matched</strong></TableCell>
                <TableCell align="right"><strong>Total Postage</strong></TableCell>
                <TableCell><strong>Filename</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((inv) => (
                <TableRow key={inv.id} hover>
                  <TableCell>{fmtDate(inv.week_ending)}</TableCell>
                  <TableCell align="right">
                    <strong>${fmt(inv.postage_per_unit, 4)}</strong>
                  </TableCell>
                  <TableCell align="right">{inv.total_units.toLocaleString()}</TableCell>
                  <TableCell align="right">{inv.total_orders.toLocaleString()}</TableCell>
                  <TableCell align="right">${fmt(inv.total_postage)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {inv.filename}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!historyLoading && history.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button size="small" onClick={fetchHistory}>
            Refresh
          </Button>
        </Box>
      )}
    </Box>
  );
}
